import React, { useState } from 'react';

import classNames from 'classnames';
import PropTypes from 'prop-types';

import { FadeInOut, FadeInOutContainer } from 'wanderu-animations';
import { modeIconMapRedesign } from 'wanderu-assets';
import Tags from 'wanderu-component-tags';
import {
    PageSection,
    Tab,
    TabContainer,
    TabList,
    TabPanel,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Typography,
    WDSButton,
} from 'wanderu-components';
import { localize } from 'wanderu-i18n';

import events from './events';
import {
    Clock,
    Dollar,
    Map,
    Star,
} from './icons.jsx';
import { formatDuration, getSearchUrls } from './utils';

import styles from './ModeComparison.scss';

const Tag = ({ text }) => {
    return (
        <Tags
            className={styles.tags}
            tags={[{ className: styles.tag, text }]}
            disabled
        />
    );
};

Tag.propTypes = {
    text: PropTypes.string,
};

const LabelCell = ({ Icon, text }) => {
    return (
        <TableCell className={styles.label}>
            <div className={styles.flex}>
                <Icon className={styles.icon} />
                {text}
            </div>
        </TableCell>
    );
};

LabelCell.propTypes = {
    Icon: PropTypes.elementType,
    text: PropTypes.string,
};

const TabComparison = ({
    data,
    destination,
    history,
    i18nMoney,
    i18nMoment,
    modes,
    origin,
    t,
}) => {
    const {
        cheapest,
        closestOrigin,
        closestDestination,
        fastest,
        shortest,
    } = data?.tags || {};

    const [activeId, setActiveId] = useState(modes[0].id);
    const onModeClick = (id, url) => {
        events.clickSearchEvent(id);
        history.push(`/en-us${url}`);
    };

    return (
        <TabContainer
            activeId={activeId}
            className={styles.tabContainer}
            onChange={setActiveId}
        >
            <TabList className={styles.tabList}>
                {modes.map(({ id }) => {
                    const Icon = modeIconMapRedesign[id];

                    return (
                        <Tab
                            className={styles.tab}
                            id={id}
                            key={`${id}-tab`}
                        >
                            <Icon
                                className={classNames(
                                    styles.tabIcon,
                                    { [styles.activeIcon]: activeId === id },
                                )}
                                height={30}
                                width={30}
                            />
                        </Tab>
                    );
                })}
            </TabList>
            <FadeInOutContainer>
                {modes.map(({ buttonText, id, url }) => {
                    const {
                        appeal,
                        distanceDestination,
                        distanceOrigin,
                        duration,
                        price,
                        wait,
                    } = data?.[id] || {};

                    return (
                        <FadeInOut
                            className={styles.fullWidth}
                            key={`${id}-fade-in-out`}
                            show={activeId === id}
                        >
                            <TabPanel
                                className={styles.tabPanel}
                                id={id}
                            >
                                <Table variant="blocks">
                                    <TableBody>
                                        <TableRow>
                                            <LabelCell Icon={Dollar} text="Avg. price" />
                                            <TableCell className={styles.right}>{cheapest?.includes(id) && <Tag text="Cheapest" />}{i18nMoney(price).format({ trailingZeroDisplay: 'stripIfInteger' })}</TableCell>
                                        </TableRow>
                                        <TableRow>
                                            <LabelCell Icon={Clock} text="Pre-departure wait" />
                                            <TableCell className={styles.right}>{shortest?.includes(id) && <Tag text="Shortest" />}{formatDuration(wait, i18nMoment, t)}</TableCell>
                                        </TableRow>
                                        <TableRow>
                                            <LabelCell Icon={Clock} text="Est. travel time" />
                                            <TableCell className={styles.right}>{fastest?.includes(id) && <Tag text="Fastest" />}{formatDuration(duration, i18nMoment, t)}</TableCell>
                                        </TableRow>
                                        <TableRow>
                                            <LabelCell Icon={Map} text={`Distance to ${origin}`} />
                                            <TableCell className={styles.right}>{closestOrigin?.includes(id) && <Tag text="Closest" />}{distanceOrigin}</TableCell>
                                        </TableRow>
                                        <TableRow>
                                            <LabelCell Icon={Map} text={`Distance to ${destination}`} />
                                            <TableCell className={styles.right}>{closestDestination?.includes(id) && <Tag text="Closest" />}{distanceDestination}</TableCell>
                                        </TableRow>
                                        <TableRow>
                                            <LabelCell Icon={Star} text="Traveler appeal" />
                                            <TableCell className={styles.right}>{appeal}</TableCell>
                                        </TableRow>
                                    </TableBody>
                                </Table>
                                {url && <WDSButton
                                    className={styles.ctaButton}
                                    color="secondary"
                                    onClick={() => onModeClick(id, url)}
                                    fullWidth
                                >
                                    {buttonText}
                                </WDSButton>}
                            </TabPanel>
                        </FadeInOut>
                    );
                })}
            </FadeInOutContainer>
        </TabContainer>
    );
};

const modeDataShape = PropTypes.shape({
    appeal: PropTypes.string,
    distanceDestination: PropTypes.string,
    distanceOrigin: PropTypes.string,
    duration: PropTypes.number,
    price: PropTypes.number,
    wait: PropTypes.number,
});

const tagsShape = PropTypes.shape({
    cheapest: PropTypes.arrayOf(PropTypes.oneOf(['bus', 'train', 'flight', 'auto'])),
    closestDestination: PropTypes.arrayOf(PropTypes.oneOf(['bus', 'train', 'flight', 'auto'])),
    closestOrigin: PropTypes.arrayOf(PropTypes.oneOf(['bus', 'train', 'flight', 'auto'])),
    fastest: PropTypes.arrayOf(PropTypes.oneOf(['bus', 'train', 'flight', 'auto'])),
    shortest: PropTypes.arrayOf(PropTypes.oneOf(['bus', 'train', 'flight', 'auto'])),
});

const dataShape = PropTypes.shape({
    auto: modeDataShape,
    bus: modeDataShape,
    flight: modeDataShape,
    tags: tagsShape,
    train: modeDataShape,
});

TabComparison.propTypes = {
    data: dataShape,
    destination: PropTypes.string,
    history: PropTypes.shape({
        push: PropTypes.func.isRequired,
    }),
    i18nMoment: PropTypes.func,
    i18nMoney: PropTypes.func,
    modes: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.string,
        })
    ),
    origin: PropTypes.string,
    t: PropTypes.func,
};

const TableComparison = ({
    data,
    destination,
    history,
    i18nMoment,
    i18nMoney,
    modes,
    origin,
    t,
}) => {
    const {
        cheapest,
        closestDestination,
        closestOrigin,
        fastest,
        shortest,
    } = data?.tags || {};

    const onModeClick = (id, url) => {
        events.clickSearchEvent(id);
        history.push(`/en-us${url}`);
    };

    return (
        <Table className={styles.table} variant="blocks">
            <TableHead>
                <TableRow variant="head">
                    <TableCell className={styles.column} variant="head" />
                    {modes.map(({ id, name }) => {
                        const Icon = modeIconMapRedesign[id];

                        return (
                            <TableCell
                                className={styles.column}
                                key={`${id}-header`}
                                size="small"
                                variant="head"
                            >
                                {Icon && <Icon className={styles.icon} height={30} width={30} />}
                                {name || t(id)}
                            </TableCell>
                        );
                    })}
                </TableRow>
            </TableHead>
            <TableBody>
                <TableRow>
                    <LabelCell Icon={Dollar} text="Avg. price" />
                    {modes.map(({ id }) => <TableCell className={styles.cell} key={`${id}-price`}>{i18nMoney(data?.[id]?.price).format({ trailingZeroDisplay: 'stripIfInteger' })}{cheapest?.includes(id) && <Tag text="Cheapest" />}</TableCell>)}
                </TableRow>
                <TableRow>
                    <LabelCell Icon={Clock} text="Pre-departure wait" />
                    {modes.map(({ id }) => <TableCell className={styles.cell} key={`${id}-wait`}>{formatDuration(data?.[id]?.wait, i18nMoment, t)}{shortest?.includes(id) && <Tag text="Shortest" />}</TableCell>)}
                </TableRow>
                <TableRow>
                    <LabelCell Icon={Clock} text="Est. travel time" />
                    {modes.map(({ id }) => <TableCell className={styles.cell} key={`${id}-duration`}>{formatDuration(data?.[id]?.duration, i18nMoment, t)}{fastest?.includes(id) && <Tag text="Fastest" />}</TableCell>)}
                </TableRow>
                <TableRow>
                    <LabelCell Icon={Map} text={`Distance to ${origin}`} />
                    {modes.map(({ id }) => <TableCell className={styles.cell} key={`${id}-origin`}>{data?.[id]?.distanceOrigin}{closestOrigin?.includes(id) && <Tag text="Closest" />}</TableCell>)}
                </TableRow>
                <TableRow>
                    <LabelCell Icon={Map} text={`Distance to ${destination}`} />
                    {modes.map(({ id }) => <TableCell className={styles.cell} key={`${id}-destination`}>{data?.[id]?.distanceDestination}{closestDestination?.includes(id) && <Tag text="Closest" />}</TableCell>)}
                </TableRow>
                <TableRow>
                    <LabelCell Icon={Star} text="Traveler appeal" />
                    {modes.map(({ id }) => <TableCell className={styles.cell} key={`${id}-appeal`}>{data?.[id]?.appeal}</TableCell>)}
                </TableRow>
                <TableRow>
                    <TableCell className={styles.buttonCell} />
                    {modes.map(({ url, id, buttonText }) => <TableCell className={styles.buttonCell} key={`${id}-cta`}>
                        {url && <WDSButton
                            className={styles.ctaButton}
                            color="secondary"
                            onClick={() => onModeClick(id, url)}
                            variant="outlined"
                            fullWidth
                        >
                            {buttonText}
                        </WDSButton>}
                    </TableCell>)}
                </TableRow>
            </TableBody>
        </Table>
    );
};

TableComparison.propTypes = {
    data: dataShape,
    destination: PropTypes.string,
    history: PropTypes.shape({
        push: PropTypes.func.isRequired,
    }),
    i18nMoment: PropTypes.func,
    i18nMoney: PropTypes.func,
    modes: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.string,
        })
    ),
    origin: PropTypes.string,
    t: PropTypes.func,
};

const ModeComparison = (props) => {
    const {
        arrivePlace,
        history,
        data,
        departDate,
        departPlace,
        i18nMoment,
        i18nMoney,
        locationData,
        t,
    } = props;

    const urls = getSearchUrls({ departDate, locationData });
    const modes = [
        {
            buttonText: 'Search Buses',
            id: 'bus',
            url: urls.bus,
        },
        {
            buttonText: 'Search Trains',
            id: 'train',
            url: urls.train,
        },
        {
            id: 'flight',
        },
        {
            id: 'auto',
            name: 'Rental',
        },
    ];

    const origin = departPlace.split(',')[0];
    const destination = arrivePlace.split(',')[0];

    const comparisonProps = {
        data,
        destination,
        history,
        i18nMoment,
        i18nMoney,
        modes,
        origin,
        t,
    };

    return (
        <PageSection
            aria-labelledby="mode-comparison"
            layoutClassName={styles.layout}
            useNewBreakpoints
        >
            <Typography
                className={styles.heading}
                id="mode-comparison"
                variant="h3"
            >
                {`How to Travel from ${origin} to ${destination}`}
            </Typography>
            <Typography paragraph>
                Every trip is different, and so is every traveler.
                We’ve compared all travel modes to help you find the one that best fits your needs.
            </Typography>

            <TabComparison {...comparisonProps} />
            <TableComparison {...comparisonProps} />
        </PageSection>
    );
};

ModeComparison.defaultProps = {
    data: {},
    departDate: new Date(),
    locationData: {},
};

ModeComparison.propTypes = {
    arrivePlace: PropTypes.string,
    data: dataShape,
    departDate: PropTypes.instanceOf(Date),
    departPlace: PropTypes.string,
    history: PropTypes.shape({
        push: PropTypes.func.isRequired,
    }),
    i18nMoment: PropTypes.func,
    i18nMoney: PropTypes.func,
    locationData: PropTypes.shape({
        arrival: PropTypes.shape({
            formattedAddress: PropTypes.string,
            placeId: PropTypes.string,
        }),
        departure: PropTypes.shape({
            formattedAddress: PropTypes.string,
            placeId: PropTypes.string,
        }),
    }),
    t: PropTypes.func,
};

export default localize(ModeComparison, 'baseApp');
